/**
 * CustomViewportModal Component
 * Week 2: Custom Viewport Creation Modal
 *
 * Features:
 * - Input validation (320-7680px width, 240-4320px height)
 * - Visual preview of viewport size
 * - Category auto-detection
 * - Real-time dimension feedback
 */

import React, { useState, useEffect, useRef } from "react";
import { useResponsiveStore } from "@/stores/editor/responsive-store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Monitor, Smartphone, Tablet, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface CustomViewportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomViewportModal: React.FC<CustomViewportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { addCustomViewport } = useResponsiveStore();

  const [name, setName] = useState("");
  const [width, setWidth] = useState<number>(1280);
  const [height, setHeight] = useState<number>(800);
  const [errors, setErrors] = useState<{
    name?: string;
    width?: string;
    height?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Week 2 Day 6.3: Focus management
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName("");
      setWidth(1280);
      setHeight(800);
      setErrors({});
      setIsSubmitting(false);

      // Week 2 Day 6.3: Focus first input when modal opens
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Validate inputs
  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (name.length > 50) {
      newErrors.name = "Name must be 50 characters or less";
    }

    // Width validation (320-7680px)
    if (!width || width < 320) {
      newErrors.width = "Width must be at least 320px";
    } else if (width > 7680) {
      newErrors.width = "Width must be 7680px or less";
    }

    // Height validation (240-4320px)
    if (!height || height < 240) {
      newErrors.height = "Height must be at least 240px";
    } else if (height > 4320) {
      newErrors.height = "Height must be 4320px or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Get category based on width
  const getCategory = (w: number): string => {
    if (w < 768) return "Mobile";
    if (w < 1024) return "Tablet";
    return "Desktop";
  };

  // Get category icon
  const getCategoryIcon = (w: number) => {
    if (w < 768) return <Smartphone className="w-4 h-4" />;
    if (w < 1024) return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;

    // Week 2 Day 6.3: Show loading state during save
    setIsSubmitting(true);

    try {
      addCustomViewport(name, width, height);
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));

      // Week 2 Day 6.4: Success toast notification
      toast.success('Custom viewport created', {
        description: `${name} (${width}×${height}px) has been added to your viewports.`,
        duration: 3000,
      });

      onClose();
    } catch (error) {
      console.error('Failed to create custom viewport:', error);
      setErrors({ name: 'Failed to create viewport' });

      // Week 2 Day 6.4: Error toast notification
      toast.error('Failed to create viewport', {
        description: 'Please try again or check your input values.',
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Week 2 Day 6.3: Handle Enter key to submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  // Calculate aspect ratio
  const aspectRatio = width && height ? (width / height).toFixed(2) : "0";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Custom Viewport</DialogTitle>
          <DialogDescription>
            Add a custom viewport with specific dimensions for responsive design testing.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Name Input */}
          <div className="grid gap-2">
            <Label htmlFor="viewport-name">Viewport Name</Label>
            <Input
              ref={nameInputRef}
              id="viewport-name"
              placeholder="e.g., Galaxy Fold, Custom Tablet"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              className={errors.name ? "border-red-500" : ""}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
              aria-required="true"
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-red-500" role="alert">
                {errors.name}
              </p>
            )}
          </div>

          {/* Width Input */}
          <div className="grid gap-2">
            <Label htmlFor="viewport-width">Width (px)</Label>
            <Input
              id="viewport-width"
              type="number"
              min={320}
              max={7680}
              placeholder="1280"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value, 10))}
              onKeyDown={handleKeyDown}
              className={errors.width ? "border-red-500" : ""}
              aria-invalid={!!errors.width}
              aria-describedby={errors.width ? "width-error" : "width-hint"}
              aria-required="true"
            />
            {errors.width && (
              <p id="width-error" className="text-sm text-red-500" role="alert">
                {errors.width}
              </p>
            )}
            <p id="width-hint" className="text-xs text-muted-foreground">
              Valid range: 320px - 7680px
            </p>
          </div>

          {/* Height Input */}
          <div className="grid gap-2">
            <Label htmlFor="viewport-height">Height (px)</Label>
            <Input
              id="viewport-height"
              type="number"
              min={240}
              max={4320}
              placeholder="800"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value, 10))}
              onKeyDown={handleKeyDown}
              className={errors.height ? "border-red-500" : ""}
              aria-invalid={!!errors.height}
              aria-describedby={errors.height ? "height-error" : "height-hint"}
              aria-required="true"
            />
            {errors.height && (
              <p id="height-error" className="text-sm text-red-500" role="alert">
                {errors.height}
              </p>
            )}
            <p id="height-hint" className="text-xs text-muted-foreground">
              Valid range: 240px - 4320px
            </p>
          </div>

          {/* Preview Information */}
          {width && height && !errors.width && !errors.height && (
            <Alert>
              <div className="flex items-center gap-2">
                {getCategoryIcon(width)}
                <AlertDescription>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Category:</span>
                      <span>{getCategory(width)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Aspect Ratio:</span>
                      <span>{aspectRatio}:1</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Orientation:</span>
                      <span>
                        {width > height
                          ? "Landscape"
                          : width < height
                            ? "Portrait"
                            : "Square"}
                      </span>
                    </div>
                  </div>
                </AlertDescription>
              </div>
            </Alert>
          )}

          {/* Visual Preview */}
          {width && height && !errors.width && !errors.height && (
            <div className="border rounded-md p-4 bg-muted/50">
              <Label className="text-xs text-muted-foreground mb-2 block">
                Visual Preview (scaled to fit)
              </Label>
              <div className="flex items-center justify-center h-32">
                <div
                  className="border-2 border-primary bg-background shadow-sm"
                  style={{
                    width: `${Math.min((width / Math.max(width, height)) * 100, 100)}%`,
                    height: `${Math.min((height / Math.max(width, height)) * 100, 100)}%`,
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                >
                  <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                    {width}×{height}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Custom viewports are saved to your browser's local storage and will
              be available across sessions.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Cancel viewport creation"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isSubmitting}
            aria-label={isSubmitting ? "Creating viewport..." : "Create custom viewport"}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Viewport"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
